import React, { useEffect, useState } from "react";
import { Spinner } from "react-bootstrap";
import { Routes, Route, Navigate, useParams } from "react-router-dom";
import { authFetch } from "./utils/authFetch";
import featureToggleService from "./services/featureToggleService";
import configurationService from "./services/configurationService";
import { LegacyLayout } from "./App";
import ModernLayout from "./components/Modern/Layout/ModernLayout";
import ModernHome from "./components/Modern/Home/ModernHome";
import { ModernIncidentDetailPage } from "./components/Modern/IncidentDetails";
import ErrorBoundary from "./components/ErrorBoundary/ErrorBoundary";
import ErrorContents from "./components/ErrorBoundary/ErrorContents";
import ModernErrorBoundary from "./components/Modern/ErrorBoundary/ErrorBoundary";
import { AuthErrorProvider } from "./contexts/AuthErrorContext";
import { logError, logWarning } from "./utils/errorLogging";

// Route Components
import ProblemForm from "./components/ProblemForm/ProblemForm";
import NewProblem from "./components/ProblemForm/NewProblem/NewProblem";
import SpeedyForm from "./components/SpeedyForm/SpeedyForm";
import Status from "./components/Status/Status";
import Profile from "./components/Profile/Profile";

import Home from "./components/Home/Home";
import Viewer from "./components/Viewer/Viewer";
import DeliveryStream from "./components/DeliveryStream/DeliveryStream";
import Escalations from "./components/Escalations";
import UserManagementApp from "./components/UserManagement/UserManagementApp";
import Configuration from "./components/Configuration/Configuration";

import ImpactViewPage from "./components/Impact/ImpactViewPage";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";

/**
 * LoadingIndicator Component
 *
 * Displays a centered loading spinner while determining feature toggle state
 */
function LoadingIndicator() {
  return (
    <div
      className="d-flex justify-content-center align-items-center"
      style={{ minHeight: "100vh" }}
    >
      <div className="text-center">
        <Spinner animation="border" role="status" variant="primary">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
        <p className="mt-3 text-muted">Loading application...</p>
      </div>
    </div>
  );
}

// Wrapper component to force ProblemForm remount when problem ID changes
function ProblemFormWrapper(props) {
  const params = useParams();
  // Use the problem ID as key to force component remount on navigation
  const key = params.id + (params.outage ? `-${params.outage}` : "");
  return <ProblemForm key={key} {...props} />;
}

/**
 * AppRoutes Component
 *
 * Handles routing with conditional rendering based on modern_ui feature toggle
 */
function AppRoutes({ NewRelic, user, loggedIn, appConfiguration }) {
  const debug = process.env.REACT_APP_DEBUG === "true";
  const browserAgent = NewRelic;

  // Environment variable override for testing
  const forceModernUIEnv = process.env.REACT_APP_FORCE_MODERN_UI;
  let forceModernUI = null;

  if (forceModernUIEnv === "true") {
    forceModernUI = true;
  } else if (forceModernUIEnv === "false") {
    forceModernUI = false;
  }
  // If undefined or empty string, forceModernUI stays null (no override)

  // Check if modern UI is enabled for the home route
  const isModernUIEnabled =
    forceModernUI !== null ? forceModernUI : user?.features?.modern_ui === true;

  // Session keepalive to prevent token expiration during idle periods
  useEffect(() => {
    if (!loggedIn || debug) {
      return; // Don't set up keepalive if not logged in or in debug mode
    }

    // Ping the session endpoint every 5 minutes to keep tokens fresh
    const sessionKeepalive = setInterval(
      () => {
        fetch("/api/v1/session")
          .then(function (response) {
            if (!response.ok) {
              if (response.status === 401) {
                // Session expired, redirect to login
                console.log(
                  "Session expired during keepalive, redirecting to login",
                );
                window.localStorage.setItem(
                  "destination",
                  window.location.href,
                );
                window.location.href = "/signin/login";
              } else {
                console.error("Session keepalive failed:", response.statusText);
              }
            }
          })
          .catch((error) => {
            logWarning("AppWrapper.sessionKeepalive", "Session keepalive failed", { error: error.message });
            NewRelic?.noticeError(error);
          });
      },
      5 * 60 * 1000,
    ); // 5 minutes in milliseconds

    // Cleanup interval on component unmount or when loggedIn changes
    return () => {
      clearInterval(sessionKeepalive);
    };
  }, [loggedIn, debug, NewRelic]);

  if (!loggedIn) {
    return (
      <LegacyLayout user={user}>
        <Row>
          <Col xs={12} xl={8}></Col>
        </Row>
      </LegacyLayout>
    );
  }

  return (
    <Routes>
      {/* Home route - conditionally render Modern or Legacy based on feature flag */}
      <Route
        path="/"
        element={
          isModernUIEnabled ? (
            <ModernLayout key="modern-home" user={user}>
              <ModernErrorBoundary newrelic={browserAgent}>
                <ModernHome user={user} newrelic={browserAgent} />
              </ModernErrorBoundary>
            </ModernLayout>
          ) : (
            <LegacyLayout user={user}>
              <Home user={user} newrelic={browserAgent} />
            </LegacyLayout>
          )
        }
      />

      {/* Past incidents route - Modern UI only, falls through to catch-all when disabled */}
      {isModernUIEnabled && (
        <Route
          path="/past-incidents"
          element={
            <ModernLayout key="modern-home" user={user}>
              <ModernErrorBoundary newrelic={browserAgent}>
                <ModernHome user={user} newrelic={browserAgent} />
              </ModernErrorBoundary>
            </ModernLayout>
          }
        />
      )}

      {/* Modern UI toggled routes: "/" (above), "/past-incidents" (above), "/problem/view/:id" (below)
          All other routes use Legacy layout regardless of feature flag */}

      {/* "/problems" renders the same page as "/"; redirect so the feature-flag
          branching lives in one place and Modern/Legacy is chosen consistently. */}
      <Route path="/problems" element={<Navigate to="/" replace />} />
      <Route
        path="/problem/edit/:id"
        element={
          <LegacyLayout user={user}>
            <ProblemFormWrapper configuration={appConfiguration} NewRelic={NewRelic} />
          </LegacyLayout>
        }
      />
      <Route
        path="/problem/edit/:id/outage/:outage"
        element={
          <LegacyLayout user={user}>
            <ProblemFormWrapper user={user} configuration={appConfiguration} NewRelic={NewRelic} />
          </LegacyLayout>
        }
      />
      {/* Incident detail route - conditionally render Modern or Legacy based on feature flag */}
      <Route
        path="/problem/view/:id"
        element={
          isModernUIEnabled ? (
            <ModernLayout user={user}>
              <ModernErrorBoundary newrelic={browserAgent}>
                <ModernIncidentDetailPage user={user} newrelic={browserAgent} />
              </ModernErrorBoundary>
            </ModernLayout>
          ) : (
            <LegacyLayout user={user}>
              <Viewer user={user} newrelic={browserAgent} />
            </LegacyLayout>
          )
        }
      />
      <Route
        path="/problem/view/:id/version/:versionId"
        element={
          <LegacyLayout user={user}>
            <Viewer user={user} newrelic={browserAgent} />
          </LegacyLayout>
        }
      />
      <Route
        path="/problem/view/:id/outage/:outage"
        element={
          <LegacyLayout user={user}>
            <Viewer user={user} newrelic={browserAgent} />
          </LegacyLayout>
        }
      />
      <Route
        path="/problem/impact/:id"
        element={
          <LegacyLayout user={user}>
            <ImpactViewPage user={user} />
          </LegacyLayout>
        }
      />
      <Route
        path="/problem/start/:id"
        element={
          <LegacyLayout user={user}>
            <NewProblem NewRelic={NewRelic} />
          </LegacyLayout>
        }
      />
      <Route
        path="/problem/new/speedy"
        element={
          <LegacyLayout user={user}>
            <SpeedyForm user={user} configuration={appConfiguration} newrelic={browserAgent} />
          </LegacyLayout>
        }
      />
      <Route
        path="/system"
        element={
          <ModernLayout user={user}>
            <Status user={user} />
          </ModernLayout>
        }
      />
      <Route
        path="/profile"
        element={
          <ModernLayout user={user}>
            <Profile user={user} />
          </ModernLayout>
        }
      />
      <Route
        path="/configuration"
        element={
          <ModernLayout user={user}>
            <Configuration user={user} configuration={appConfiguration} />
          </ModernLayout>
        }
      />
      <Route
        path="/configuration/deliverystream/new"
        element={
          <ModernLayout user={user}>
            <DeliveryStream user={user} configuration={appConfiguration} />
          </ModernLayout>
        }
      />
      <Route
        path="/configuration/deliverystream/:id"
        element={
          <ModernLayout user={user}>
            <DeliveryStream user={user} configuration={appConfiguration} />
          </ModernLayout>
        }
      />
      <Route
        path="/manage/escalations"
        element={
          <ModernLayout user={user}>
            <Escalations />
          </ModernLayout>
        }
      />

      {/* Deity-only routes */}
      {user && user.privileges && user.privileges["deity"] && (
        <Route
          path="/manage/users"
          element={
            <ModernLayout user={user} wideContent={true}>
              <UserManagementApp user={user} />
            </ModernLayout>
          }
        />
      )}

      {/* Catch-all redirect */}
      <Route path="*" element={<Navigate to="/" />} />
    </Routes>
  );
}

/**
 * AppWrapper Component
 *
 * Loads user data and sets up routing with route-level feature toggles
 */
function AppWrapper({ NewRelic }) {
  const debug = process.env.REACT_APP_DEBUG === "true";
  const [user, setUser] = useState(null);
  const [loggedIn, setLoggedIn] = useState(debug);
  const [loading, setLoading] = useState(true);
  const [appConfiguration, setAppConfiguration] = useState({
    products: [],
    slack: { channels: [] },
    configuration: {},
    portfolios: [],
    deliveryStreams: [],
    isLoading: true,
    lastFetched: null,
  });

  const debugUser = {
    name: "DEBUG",
    email: "notloggedin@coxautoinc.com",
    title: "Invisible Rodeo Clown Man",
    loggedIn: false,
    osm: true,
    // US2028928: UI hint only. The backend re-enforces the launch-admin allowlist
    // on every /modern-ui-launch call, and notloggedin@ is not in it, so this
    // debug user can see the toggle locally but cannot actually flip it (403).
    modern_ui_launch_admin: true,
    privileges: {
      editor: true,
      deity: true,
      search: true,
      create: true,
      archive: true,
      configuration: true,
      admin: true,
      administrator: true,
      management: true,
      reporting: true,
      analytics: true,
      monitoring: true,
      maintenance: true,
      security: true,
      audit: true,
      export: true,
      import: true,
      delete: true,
      modify: true,
      view: true,
      read: true,
      write: true,
      execute: true,
      impact: true,
    },
    features: {
      onepagesummary: false,
      notifications: true,
      advancedreporting: true,
      betafeatures: true,
      chronological: true,
      modern_ui: true,
    },
  };

  useEffect(() => {
    const loadUser = async () => {
      if (debug) {
        // Debug mode - use debug user
        setUser(debugUser);
        setLoggedIn(true);
        localStorage.setItem("user", JSON.stringify(debugUser));
        featureToggleService.setUser(debugUser);

        // Load configuration
        try {
          const config = await configurationService.loadConfiguration();
          setAppConfiguration(config);
        } catch (error) {
          logError("AppWrapper.loadConfiguration", error, { mode: "debug" });
          setAppConfiguration({
            products: [],
            slack: { channels: [] },
            configuration: {},
            portfolios: [],
            deliveryStreams: [],
            isLoading: false,
            lastFetched: null,
          });
        }
        setLoading(false);
      } else {
        // Production mode - fetch session
        try {
          const res = await authFetch("/api/v1/session", {
            method: "GET",
            credentials: "include",
          });
          const result = await res.json();

          const sessionUser = {
            name: result.name,
            email: result.email,
            privileges: result.privileges,
            features: result.features,
            title: result.title,
            modern_ui_launch_admin: result.modern_ui_launch_admin,
            loggedIn: true,
          };

          localStorage.setItem("user", JSON.stringify(sessionUser));
          setUser(sessionUser);
          featureToggleService.setUser(sessionUser);

          // Load configuration
          try {
            const config = await configurationService.loadConfiguration();
            setAppConfiguration(config);
          } catch (error) {
            logError("AppWrapper.loadConfiguration", error, { mode: "production" });
            NewRelic?.noticeError(error);
            setAppConfiguration({
              products: [],
              slack: { channels: [] },
              configuration: {},
              portfolios: [],
              deliveryStreams: [],
              isLoading: false,
              lastFetched: null,
            });
          }

          setLoggedIn(true);
          setLoading(false);

          const destination = window.localStorage.getItem("destination");
          if (destination) {
            window.localStorage.setItem("last_destination", destination);
            window.localStorage.removeItem("destination");
            window.location.href = destination;
          }
        } catch (error) {
          logError("AppWrapper.sessionLoad", error);

          // If we get a 401 during initial session load, redirect to login
          // This handles the case where AuthErrorProvider isn't mounted yet
          if (error.status === 401) {
            logWarning("AppWrapper.sessionLoad", "Session expired, redirecting to login");
            window.localStorage.setItem("destination", window.location.href);
            window.location.href = "/signin/login";
            return; // Don't set loading to false, we're redirecting
          }

          NewRelic?.noticeError(error);
          setLoading(false);
        }
      }
    };

    loadUser();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debug]);

  // Set New Relic user identity when user resolves (or clears on logout).
  // This attaches user info to ALL NR events globally, so individual
  // tracking functions do not need userProfile parameters.
  useEffect(() => {
    NewRelic?.setCustomAttribute('appVersion', global.appVersion);
    if (user) {
      NewRelic?.setUserId(user.email);
      NewRelic?.setCustomAttribute('userName', user.name);
      NewRelic?.setCustomAttribute('userPrivileges', Object.keys(user.privileges || {}).filter(k => user.privileges[k]).join(','));
      NewRelic?.setCustomAttribute('uiMode', user.features?.modern_ui ? 'modern' : 'classic');
    } else {
      NewRelic?.setUserId(null);
      NewRelic?.setCustomAttribute('userName', null);
      NewRelic?.setCustomAttribute('userPrivileges', null);
      NewRelic?.setCustomAttribute('uiMode', null);
    }
  }, [user, NewRelic]);

  // Show loading while user is being loaded
  if (loading) {
    return <LoadingIndicator />;
  }

  return (
    <AuthErrorProvider>
      <ErrorBoundary
        newrelic={NewRelic}
        fallback={<ErrorContents>Something Went Wrong</ErrorContents>}
      >
        <AppRoutes
          NewRelic={NewRelic}
          user={user}
          loggedIn={loggedIn}
          appConfiguration={appConfiguration}
        />
      </ErrorBoundary>
    </AuthErrorProvider>
  );
}

export default AppWrapper;
